import { Button, Flex, type FlexProps, Tooltip, Typography } from "antd"
import classNames from "classnames"
import type { UploadedResource } from "~/common/media/uploadedResources"
import type { ReactNode } from "react"
import type { VideoOptions } from "~/common/constants"
import { getVideoGenerationPointCost } from "~/common/billing/pointCosts"
import { getVideoModelDisabledReasons } from "~/common/video/videoModels"
import {
  AnalyticsAction,
  type AnalyticsContextProps,
} from "~/components/Analytics"
import { usePointBalanceGuard } from "~/components/PointBalanceGuard"
import { VideoParameterSelect } from "~/components/VideoParameterSelect"
import {
  VideoGenerateConfirm,
  type VideoGenerateConfirmProps,
} from "~/components/VideoGenerateConfirm"
import styles from "./index.css"

const { Text } = Typography

type GenerateConfirmProps = Omit<
  VideoGenerateConfirmProps,
  "children" | "onConfirm" | "model" | "pointCost"
>

type VideoGenerateFooterProps = FlexProps & {
  /** Caller must confirm and trim excess images before accepting the model change. */
  allowImageOverflow?: boolean
  analytics?: AnalyticsContextProps & {
    action: string
    fields?: Record<string, string>
  }
  hideOptions?: (keyof VideoOptions)[]
  durationReadOnly?: boolean
  actions?: ReactNode
  disabled?: boolean
  disabledReason?: string
  loading?: boolean
  /** Return false to handle an access requirement before checking points. */
  beforeGenerate?: () => boolean
  onGenerate: () => void
  onVideoOptionsChange: (options: VideoOptions) => void
  generateConfirm?: boolean | GenerateConfirmProps
  referenceImageCount: number
  referenceResources?: UploadedResource[]
  videoOptions: VideoOptions
}

export const VideoGenerateFooter = (props: VideoGenerateFooterProps) => {
  const {
    analytics,
    allowImageOverflow = false,
    hideOptions,
    durationReadOnly = false,
    actions,
    className,
    disabled = false,
    disabledReason,
    generateConfirm,
    loading = false,
    beforeGenerate,
    onGenerate,
    onVideoOptionsChange,
    referenceImageCount,
    referenceResources,
    videoOptions,
    ...rest
  } = props
  const { ensureSufficientPoints } = usePointBalanceGuard({
    ...(hideOptions?.includes("model") ? { canChangeModel: false } : {}),
    ...(durationReadOnly || hideOptions?.includes("duration")
      ? { canShortenDuration: false }
      : {}),
    ...(hideOptions?.includes("resolution")
      ? { canChangeResolution: false }
      : {}),
  })
  const updateVideoOptions = (options: Partial<VideoOptions>) => {
    onVideoOptionsChange({
      ...videoOptions,
      ...options,
    })
  }

  const modelDisabledReasons = getVideoModelDisabledReasons({
    ...(referenceResources
      ? { resources: referenceResources }
      : { referenceImageCount }),
    duration: videoOptions.duration,
    allowImageOverflow,
  })

  const pointCostOptions = {
    duration: videoOptions.duration,
    model: videoOptions.model,
    referenceImageCount,
    resolution: videoOptions.resolution,
  }
  const pointCost = getVideoGenerationPointCost(pointCostOptions)

  const pointCostText = (
    <>
      预计消耗&nbsp;
      <Text style={{ color: "var(--ant-color-primary)" }}>{pointCost}</Text>
      &nbsp;积点
    </>
  )
  const generateTooltipTitle = disabledReason ? (
    <Flex vertical>
      <span>{disabledReason}</span>
      <span>{pointCostText}</span>
    </Flex>
  ) : (
    pointCostText
  )

  const generateWithPointGuard = async () => {
    if (beforeGenerate && !beforeGenerate()) return
    if (!(await ensureSufficientPoints(pointCost, pointCostOptions))) {
      return
    }

    onGenerate()
  }

  const generateConfirmProps =
    typeof generateConfirm === "object" ? generateConfirm : undefined

  const button = (
    <Button
      disabled={disabled}
      loading={loading}
      onClick={generateConfirm ? undefined : generateWithPointGuard}
      type="primary"
    >
      生成视频
    </Button>
  )

  const generateButton =
    disabled && disabledReason ? (
      <Tooltip title={generateTooltipTitle}>{button}</Tooltip>
    ) : generateConfirm ? (
      <VideoGenerateConfirm
        {...generateConfirmProps}
        model={videoOptions.model}
        pointCost={pointCost}
        onConfirm={generateWithPointGuard}
      >
        {button}
      </VideoGenerateConfirm>
    ) : (
      <Tooltip
        destroyOnHidden
        getPopupContainer={(triggerNode) => triggerNode}
        open
        title={generateTooltipTitle}
      >
        {button}
      </Tooltip>
    )

  return (
    <>
      <Flex
        align="center"
        className={classNames(styles.footer, className)}
        gap={8}
        justify="space-between"
        {...rest}
        wrap={false}
      >
        <Flex
          align="center"
          gap={8}
        >
          <VideoParameterSelect
            wrap={false}
            actions={actions}
            durationReadOnly={durationReadOnly}
            hideOptions={hideOptions}
            modelDisabledReasons={modelDisabledReasons}
            value={videoOptions}
            onChange={updateVideoOptions}
          />
        </Flex>
        <Flex
          align="center"
          gap={8}
        >
          {analytics ? (
            <AnalyticsAction
              action={analytics.action}
              category={analytics.category}
              fields={analytics.fields}
              prefix={analytics.prefix}
            >
              {generateButton}
            </AnalyticsAction>
          ) : (
            generateButton
          )}
        </Flex>
      </Flex>
    </>
  )
}
